import { FlexibleButtonIconModel } from '@services/flexible_component/mod';
import { ExternalSvg } from '@components/external_svg.component';
import { Routeable } from './_flexible_identifier_routeable.component';
import { ActionCard } from '@components/action_card.component';
import { Icon, IconDictionary } from '@cellula/react';
import { ErrorBoundary } from '@components/error_boundary.component';
import { Fragment } from 'react';
import { BreakPoints, useBreakpoint } from '@cellula/react';
import { useService } from '@visiba/instantiation';
import { AnalyticService } from '@application/analytic/analytic.service';
import { useAnalyticContext } from '@components/analytic_link/analytic_link.provider';

interface Props {
	component: FlexibleButtonIconModel;
	__TEMP__on_OPW_WebContentButton_proxy_invoke?: (flexibleIdentifierData: {
		url: string;
		openUrlWith: string;
		requiresAuthentication: boolean;
	}) => void;
}

function getIcon(icon: NonNullable<FlexibleButtonIconModel['icon']>): JSX.Element {
	switch (icon.type) {
		case 'local':
			return (
				<ErrorBoundary fallback={<Fragment />}>
					<Icon size='small' type={icon.origin as keyof IconDictionary} />
				</ErrorBoundary>
			);
		case 'svgUrl':
			return <ExternalSvg imgSrc={icon.origin} size={32} />;
		case 'pngUrl':
			return <img src={icon.origin} height={32} alt='' />;
		default: {
			const shouldNeverHappen: never = icon.type;

			return shouldNeverHappen;
		}
	}
}

export function FlexibleActionCard({ component, __TEMP__on_OPW_WebContentButton_proxy_invoke, ...rest }: Props): JSX.Element {
	const iconElement = component.icon != null ? getIcon(component.icon) : undefined;
	const breakpoint = useBreakpoint();
	const analyticService = useService(AnalyticService);
	const analyticContext = useAnalyticContext();

	function analyticQuirk_handleButtonClick(): void {
		//                                         If the url was not parsed through "hostnameIsWithinSameDomain"
		//                                         (within flexible_component.service), it'll not be an absolute.
		//                                         And if it's not absolute it should count as an external button.
		if (component.openUrlWith !== 'SameTab' || component.url.includes('http')) {
			analyticService.trackEvent(analyticContext, `Clicked: External button`);

			return;
		}

		let type: 'booking' | 'messaging' | 'drop-in' | 'triage' | 'unknown';
		if (/\/booking/i.test(component.url)) {
			type = 'booking';
		} else if (/\/message/i.test(component.url)) {
			type = 'messaging';
		} else if (/\/dropin/i.test(component.url)) {
			type = 'drop-in';
		} else {
			type = 'unknown';
		}
		analyticService.trackEvent(analyticContext, `Clicked: button ${type}`);
	}

	return (
		<Routeable
			__TEMP__on_OPW_WebContentButton_proxy_invoke={__TEMP__on_OPW_WebContentButton_proxy_invoke}
			url={component.url}
			requiresAuthentication={component.requiresAuthentication}
			openWith={component.openUrlWith}
			{...rest}
		>
			<ActionCard
				data-testid='FlexibleActionCard'
				variant={breakpoint === BreakPoints.Small ? 'slim' : 'chunky'}
				title={component.title}
				icon={iconElement}
				subTitle={component.subtitle}
				isStatic
				onClick={analyticQuirk_handleButtonClick}
			/>
		</Routeable>
	);
}
